import { SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { deleteCampaign as deleteCampaignApi, updateCampaign } from 'api/campaigns';
import * as DuplicateCampaignModal from 'components/duplicate-campaign-modal';
import * as Form from 'components/form';
import { Permissions } from 'components/permissions';
import { CampaignPermission } from 'components/permissions/PermissionEnums';
import * as SettingsCard from 'components/settings-card';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useQueryParams } from 'customHooks/useQueryParams';
import { accountQueryKeys } from 'queries/account-query-keys';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface OtherFormValues {
    isListed: boolean;
}

const CampaignOtherSettings = () => {
    const toast = useToast();
    const history = useHistory();

    const queryParams = useQueryParams<{ duplicate: string }>();
    const duplicateDefaultOpen = queryParams.duplicate === 'true';

    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { mutate: deleteCampaign } = useMutation({
        mutationFn: (campaignId: number) => deleteCampaignApi(campaignId),
        onSuccess: (_, campaignId) => {
            toast.success('Deleted campaign successfully!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
                refetchType: 'none',
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.campaigns(accountId),
                type: 'all',
            });
            history.replace(`/accounts/${accountId}/campaigns`);
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to delete campaign'),
    });
    const { data: campaign } = useCurrentCampaign();
    const methods = Form.useGivebutterForm<OtherFormValues>({
        defaultValues: {
            isListed: !(campaign?.public ?? false),
        },
    });
    const { control, path, handleSubmit } = methods;
    const { mutate: submit } = useMutation({
        mutationFn: (values: OtherFormValues) => {
            const data = {
                public: !values.isListed,
            };

            return updateCampaign({ campaignId: campaign?.id!, data });
        },
        onSuccess: () => toast.success('Updated campaign successfully!'),
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update campaign'),
    });

    return (
        <Form.Provider {...methods}>
            <form onSubmit={handleSubmit(submit as SubmitHandler<OtherFormValues>)}>
                <SettingsCard.Root>
                    <SettingsCard.LeftSide>
                        <SettingsCard.Heading>Other</SettingsCard.Heading>
                        <p className={'text-sm text-gray-700'}>
                            Duplicate, delete, and manage the visibility of this campaign.
                        </p>
                    </SettingsCard.LeftSide>
                    <SettingsCard.RightSide>
                        <div className={'flex flex-col gap-4'}>
                            <div className={'flex flex-col gap-2'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <Form.Label className={'text-base'} name={path('isListed')}>
                                        Make this campaign unlisted
                                    </Form.Label>
                                    <Form.Switch control={control} name={'isListed'} />
                                </div>
                                <p className={'text-sm text-gray-700'}>
                                    Turn this on if you'd like to hide your campaign from public
                                    view and keep it unlisted. Only those with the URL will be able
                                    to find it.
                                </p>
                            </div>
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                            <Separator />
                            <div className={'flex flex-wrap gap-4'}>
                                <Permissions
                                    allowed={[CampaignPermission.CREATE]}
                                    fallback={
                                        <Button variant="solid" disabled={true}>
                                            Duplicate Campaign
                                        </Button>
                                    }
                                >
                                    {!!campaign && campaign.type !== 'general' && (
                                        <DuplicateCampaignModal.Root
                                            campaign={campaign}
                                            defaultOpen={duplicateDefaultOpen}
                                            onDuplicateSuccess={campaign => {
                                                if (!duplicateDefaultOpen) return;
                                                const { id, account_id } = campaign;
                                                history.push(
                                                    `/accounts/${account_id}/campaigns/${id}`,
                                                );
                                            }}
                                        >
                                            <DuplicateCampaignModal.Trigger asChild={true}>
                                                <Button variant="solid">Duplicate Campaign</Button>
                                            </DuplicateCampaignModal.Trigger>
                                        </DuplicateCampaignModal.Root>
                                    )}
                                </Permissions>
                                <Permissions
                                    allowed={[CampaignPermission.DELETE]}
                                    fallback={
                                        <Button variant="solid" color={'error'} disabled={true}>
                                            Delete Campaign
                                        </Button>
                                    }
                                >
                                    <AlertDialog.Root type={'error'}>
                                        <AlertDialog.Trigger asChild={true}>
                                            <Button variant="solid" color={'error'}>
                                                Delete campaign
                                            </Button>
                                        </AlertDialog.Trigger>
                                        <AlertDialog.Portal>
                                            <AlertDialog.Overlay />
                                            <AlertDialog.Content>
                                                <AlertDialog.Header>
                                                    <AlertDialog.Icon />
                                                    <AlertDialog.Close />
                                                </AlertDialog.Header>
                                                <AlertDialog.Body>
                                                    <AlertDialog.Title>
                                                        Delete campaign
                                                    </AlertDialog.Title>
                                                    <AlertDialog.Description>
                                                        You will not be able to undo this action.
                                                        Any SMS keywords assigned to this campaign
                                                        will be removed.
                                                    </AlertDialog.Description>
                                                </AlertDialog.Body>
                                                <AlertDialog.Footer>
                                                    <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                    <AlertDialog.Action
                                                        onClick={() =>
                                                            deleteCampaign(campaign?.id!)
                                                        }
                                                    >
                                                        Delete
                                                    </AlertDialog.Action>
                                                </AlertDialog.Footer>
                                            </AlertDialog.Content>
                                        </AlertDialog.Portal>
                                    </AlertDialog.Root>
                                </Permissions>
                            </div>
                        </div>
                    </SettingsCard.RightSide>
                </SettingsCard.Root>
            </form>
        </Form.Provider>
    );
};

export { CampaignOtherSettings };
